/**
 * Medplum hooks for Template Library CRUD operations.
 *
 * All templates are stored as FHIR Questionnaire resources in Medplum.
 * This hook provides search, create, update, duplicate, and archive operations.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMedplum } from '@medplum/react-hooks';
import type { Questionnaire, Bundle } from '@medplum/fhirtypes';
import {
  type TemplateCategory,
  type TemplateListItem,
  type TemplateStatus,
} from '../types';
import { isMockMedplumMode } from '../../../../lib/isMockMedplumMode';
import { getTemplateCategory, setTemplateCategory, isDefaultTemplate } from '../utils/templateTags';
import { getUserFacingErrorMessage } from '@/lib/userFacingError';
import { omit } from '@/lib/omit';
import { searchQuestionnairesByCategory } from '../utils/searchQuestionnaireTemplates';
import { sortByTemplateTitle } from '../utils/templateListHelpers';
import { getMockTemplates } from '../mockTemplates';
import { getAllLocalTemplates, subscribeToLocalTemplates } from '../localTemplateStore';

// ---------------------------------------------------------------------------
// Hook: useTemplates
// ---------------------------------------------------------------------------

interface UseTemplatesOptions {
  /** Filter templates by category. If undefined, fetch all. */
  category?: TemplateCategory;
  /** Filter by status. Defaults to showing active + draft. */
  statuses?: TemplateStatus[];
  /** Search query (filters by title). */
  search?: string;
  /** Sort field. */
  sortBy?: 'lastUpdated' | 'title';
  /** Sort direction. */
  sortOrder?: 'asc' | 'desc';
  /** Page size for pagination. */
  pageSize?: number;
  /** Current page (0-based). */
  page?: number;
}

interface UseTemplatesResult {
  templates: TemplateListItem[];
  total: number;
  loading: boolean;
  error: string | null;
  /** Refetch from server; returns a Promise so callers can await list update. */
  refetch: () => Promise<void>;
}

/**
 * Hook to search and list templates from Medplum.
 */
export function useTemplates(options: UseTemplatesOptions = {}): UseTemplatesResult {
  const medplum = useMedplum();
  const [templates, setTemplates] = useState<TemplateListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const {
    category,
    statuses = ['active', 'draft'],
    search,
    sortBy = 'title',
    sortOrder = 'asc',
    pageSize = 10,
    page = 0,
  } = options;
  const statusesKey = statuses.join(',');

  const doFetch = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const searchParams: Record<string, string> = {
        _count: String(category ? 200 : pageSize),
        _offset: category ? '0' : String(page * pageSize),
        _total: 'accurate',
      };
      if (statusesKey) {
        searchParams.status = statusesKey;
      }
      if (search && search.trim()) {
        searchParams['title:contains'] = search.trim();
      }
      if (sortBy === 'lastUpdated') {
        searchParams._sort = sortOrder === 'desc' ? '-_lastUpdated' : '_lastUpdated';
      } else if (sortBy === 'title') {
        searchParams._sort = sortOrder === 'desc' ? '-title' : 'title';
      }

      let questionnaires: Questionnaire[];
      let totalCount: number;
      if (category) {
        const allMatching = sortByTemplateTitle(
          await searchQuestionnairesByCategory(medplum, category, searchParams),
          sortOrder,
        );
        totalCount = allMatching.length;
        questionnaires = allMatching.slice(page * pageSize, (page + 1) * pageSize);
      } else {
        const bundle: Bundle = await medplum.search('Questionnaire', searchParams);
        questionnaires = (bundle.entry ?? [])
          .map((entry) => entry.resource as Questionnaire)
          .filter(Boolean);
        totalCount = bundle.total ?? questionnaires.length;
      }

      const items: TemplateListItem[] = questionnaires.map(questionnaireToTemplateListItem);
      const mockAll = getMockTemplates();
      const mockFiltered = category ? mockAll.filter((t) => t.category === category) : mockAll;
      const allowMockFallback = isMockMedplumMode();
      if (items.length > 0) {
        setTemplates(items);
        setTotal(totalCount);
      } else if (allowMockFallback) {
        setTemplates(mockFiltered);
        setTotal(mockFiltered.length);
      } else {
        setTemplates([]);
        setTotal(totalCount);
      }
    } catch (err) {
      setError(getUserFacingErrorMessage(err, 'Failed to fetch templates'));
      const mockAll = getMockTemplates();
      const mockFiltered = category ? mockAll.filter((t) => t.category === category) : mockAll;
      if (isMockMedplumMode()) {
        setTemplates(mockFiltered);
        setTotal(mockFiltered.length);
      } else {
        setTemplates([]);
        setTotal(0);
      }
    } finally {
      setLoading(false);
    }
  }, [medplum, category, statusesKey, search, sortBy, sortOrder, pageSize, page]);

  // Re-fetch when local template store changes
  const [, setLocalVersion] = useState(0);
  useEffect(() => {
    return subscribeToLocalTemplates(() => setLocalVersion((v) => v + 1));
  }, []);

  useEffect(() => {
    doFetch();
  }, [doFetch]);

  const refetch = useCallback((): Promise<void> => doFetch(), [doFetch]);

  // Merge local templates into results
  const mergedTemplates = useMemo(() => {
    const localAll = getAllLocalTemplates();
    const localFiltered = category ? localAll.filter((t) => t.category === category) : localAll;
    const localIds = new Set(localFiltered.map((t) => t.id));
    const serverWithoutDupes = templates.filter((t) => !localIds.has(t.id));
    // Unsaved local drafts only on the first server page so pagination matches Medplum totals.
    if (page > 0) {
      return sortByTemplateTitle(serverWithoutDupes, sortOrder);
    }
    return sortByTemplateTitle([...localFiltered, ...serverWithoutDupes], sortOrder);
  }, [templates, category, page, sortOrder]);

  /** Total Questionnaires matching the current Medplum search (for pagination). */
  return { templates: mergedTemplates, total, loading, error, refetch };
}

// ---------------------------------------------------------------------------
// Hook: useTemplateCRUD
// ---------------------------------------------------------------------------

interface UseTemplateCRUDResult {
  createTemplate: (questionnaire: Questionnaire, category: TemplateCategory) => Promise<Questionnaire>;
  updateTemplate: (questionnaire: Questionnaire) => Promise<Questionnaire>;
  duplicateTemplate: (id: string) => Promise<Questionnaire>;
  archiveTemplate: (id: string) => Promise<Questionnaire>;
  unarchiveTemplate: (id: string) => Promise<Questionnaire>;
  deleteTemplate: (id: string) => Promise<void>;
  fetchTemplate: (id: string) => Promise<Questionnaire>;
}

/**
 * Hook providing CRUD operations for templates via Medplum.
 */
export function useTemplateCRUD(): UseTemplateCRUDResult {
  const medplum = useMedplum();

  const createTemplate = useCallback(
    async (questionnaire: Questionnaire, category: TemplateCategory): Promise<Questionnaire> => {
      const tagged = setTemplateCategory(
        { ...questionnaire, resourceType: 'Questionnaire' },
        category,
      );
      return medplum.createResource(tagged);
    },
    [medplum],
  );

  const updateTemplate = useCallback(
    async (questionnaire: Questionnaire): Promise<Questionnaire> => {
      return medplum.updateResource(questionnaire);
    },
    [medplum],
  );

  const duplicateTemplate = useCallback(
    async (id: string): Promise<Questionnaire> => {
      const original = await medplum.readResource('Questionnaire', id);
      // Create a copy without id and meta (Medplum will assign new id/versionId)
      const rest = omit(original, 'id', 'meta');
      const copy: Questionnaire = {
        ...rest,
        resourceType: 'Questionnaire',
        title: `${original.title ?? 'Untitled'} (Copy)`,
        name: original.name ? `${original.name}-copy` : undefined,
        status: 'draft', // Duplicated templates start as draft
      };
      // Preserve the original's category so the duplicate appears in the same tab
      const category = getTemplateCategory(original) ?? 'administrative';
      const tagged = setTemplateCategory(copy, category);
      return medplum.createResource(tagged);
    },
    [medplum],
  );

  const archiveTemplate = useCallback(
    async (id: string): Promise<Questionnaire> => {
      const resource = await medplum.readResource('Questionnaire', id);
      return medplum.updateResource({ ...resource, status: 'retired' });
    },
    [medplum],
  );

  const unarchiveTemplate = useCallback(
    async (id: string): Promise<Questionnaire> => {
      const resource = await medplum.readResource('Questionnaire', id);
      return medplum.updateResource({ ...resource, status: 'active' });
    },
    [medplum],
  );

  const deleteTemplate = useCallback(
    async (id: string): Promise<void> => {
      await medplum.deleteResource('Questionnaire', id);
    },
    [medplum],
  );

  const fetchTemplate = useCallback(
    async (id: string): Promise<Questionnaire> => {
      return medplum.readResource('Questionnaire', id);
    },
    [medplum],
  );

  return useMemo(
    () => ({
      createTemplate,
      updateTemplate,
      duplicateTemplate,
      archiveTemplate,
      unarchiveTemplate,
      deleteTemplate,
      fetchTemplate,
    }),
    [createTemplate, updateTemplate, duplicateTemplate, archiveTemplate, unarchiveTemplate, deleteTemplate, fetchTemplate],
  );
}

// ---------------------------------------------------------------------------
// Helper: convert Questionnaire to TemplateListItem (exported for editor page)
// ---------------------------------------------------------------------------

export function questionnaireToTemplateListItem(q: Questionnaire): TemplateListItem {
  const category = getTemplateCategory(q) ?? 'administrative';
  const fhirStatus = q.status ?? 'draft';
  const status = (fhirStatus === 'retired' ? 'retired' : fhirStatus) as TemplateStatus;

  return {
    id: q.id ?? '',
    title: q.title ?? q.name ?? 'Untitled Template',
    description: q.description ?? '',
    status,
    category,
    lastUpdated: q.meta?.lastUpdated ?? '',
    versionId: q.meta?.versionId ?? '',
    isDefault: isDefaultTemplate(q),
    // TODO: Compute inUse by querying QuestionnaireResponse?questionnaire=Questionnaire/{id}&_summary=count
    // Per PRD, templates in active use should show an indicator.
    inUse: false,
    resource: q,
  };
}
